import capitalize from 'lodash/capitalize';
import { useState } from 'react';
import { Select } from '@round/whitelabel-ui';
import { StylesConfig } from 'react-select';
import { GenericDropdownOption } from 'App.types';
import { creatorbase } from '@round/api';
import { CellContext } from '@tanstack/react-table';
import { BasePostTableMeta } from 'Modules/Plans/Posts/types';
import { getTableMetaHelper, Skeleton } from '@round/ui-kit';
import { showNotification } from 'helpers';

type Row = creatorbase.Post;

type Context<T extends Row> = CellContext<T, creatorbase.PostPaymentStatus>;

const getTableMeta = getTableMetaHelper<BasePostTableMeta<Row>>();

const OPTIONS: GenericDropdownOption<creatorbase.PostPaymentStatus>[] = (['PAID', 'UNPAID'] as const).map((value) => ({
    value,
    label: capitalize(value.toLowerCase()),
}));

const ManualPaymentStatusCell = <T extends Row>({ table, row, getValue }: Context<T>) => {
    const { isLoading, updatePost } = getTableMeta(table);

    const initialStatus = getValue();
    const [value, setValue] = useState<creatorbase.PostPaymentStatus>(initialStatus);
    const selectedOption =
        // We'll fallback to UNPAID if there is an automated status like REQUESTED or CANCELLED.
        // This cell will only be shown to users without payments enabled, so if there is somehow a request status we can ignore it.
        OPTIONS.find((option) => option.value === value) || OPTIONS.find((option) => option.value === 'UNPAID');

    const handleUpdate = async (value: creatorbase.PostPaymentStatus) => {
        const errorMessage = 'Failed to update payment status';
        if (!updatePost) {
            return;
        }

        try {
            const response = await updatePost(row.original.id, {
                payment_status: value,
            });

            if (response.status === 200) {
                setValue(response.data.payment_status);
                showNotification('Payment status updated successfully', 'info');
                return;
            }

            setValue(initialStatus);
            showNotification(errorMessage, 'error');
        } catch {
            setValue(initialStatus);
            showNotification(errorMessage, 'error');
        }
    };

    const selectStyles: StylesConfig = {
        container: (base) => ({
            ...base,
            width: '10.75rem',
        }),
        control: (base) => ({
            ...base,
            width: 'max-content',
            padding: '0.25rem',
            transition: 'none',
            background: 'transparent',
            border: '1px solid transparent',
            ':hover': {
                padding: '0.25rem',
            },
        }),
        singleValue: (base) => ({
            ...base,
            position: 'static',
            top: '0',
            transform: 'none',
            fontSize: '0.75rem',
            color: value === 'PAID' ? '#079455' : '',
        }),
        menu: (base) => ({
            ...base,
            width: '12.5rem',
        }),
        option: (base) => ({
            ...base,
            fontWeight: 500,
            fontSize: '0.75rem',
            // text-base color
            color: '#242424',
        }),
    };

    if (isLoading) {
        return <Skeleton />;
    }

    return (
        <Select
            options={OPTIONS}
            value={selectedOption}
            onChange={(opt) => {
                if (!opt) {
                    return;
                }
                setValue(opt.value);
                handleUpdate(opt.value);
            }}
            isSearchable={false}
            isClearable={false}
            styles={selectStyles}
        />
    );
};

export default ManualPaymentStatusCell;
